import React, { useEffect, useState } from 'react';
import DataTable from '@salesforce/design-system-react/module/components/data-table';
import DataTableCell from '@salesforce/design-system-react/module/components/data-table/cell';
import Icon from '@salesforce/design-system-react/module/components/icon';
import DataTableColumn from '@salesforce/design-system-react/module/components/data-table/column';
import { useDispatch, useSelector } from 'react-redux';
import { getNumbersWithDecimalPlaces, getNumberWithCommas } from '../../../utilities/money';
import { getProtocolsOfAccount, protocolsSelector } from '../../../store/protocols/slice';
import BarChartComponent from '../../../components/Charts/BarChart';
import LightningIcon from '../../../assets/img/lightning.png';
import moment from 'moment';

/**
 * Custom wallet cell with dropdown.
 */
const ProtocolCell = ({ children, ...props }) => {
  const { item, handleExpand } = props;

  if (item.isChildren) {
    return (
      <DataTableCell {...props}>
        <div className="slds-col_padded">{children}</div>
      </DataTableCell>
    );
  }

  return (
    <DataTableCell
      title="Wallet"
      className="slds-m-right_xx-small slds-m-vertical_large"
      role="gridcell"
    >
      <div
        onClick={(i) => (item?.isChildren ? null : handleExpand(i, item))}
        className="slds-col_padded"
      >
        <span
          className="slds-m-right_large"
          style={{
            marginRight: '0.5rem',
            width: '9rem',
            display: 'inline-block',
          }}
        >
          {item.isChildren ? (
            <img src={`/assets/icons/tokens/${children}.svg`} alt={children} />
          ) : (
            <img className="protocols-logo" src={item.logo_url} width="25" alt={children} />
          )}{' '}
          {children}
        </span>
        {!item.isChildren && (
          <Icon
            assistiveText={{ label: 'Account' }}
            category="utility"
            name="chevrondown"
            size="xx-small"
            className="slds-m-left_small"
            style={{ cursor: 'pointer' }}
          />
        )}
      </div>
    </DataTableCell>
  );
};

ProtocolCell.displayName = DataTableCell.displayName;

/**
 * Custom price cell to display formatted date
 */
const CustomPriceCell = ({ children, ...props }) => {
  const { item } = props;
  if (item.isChildren) {
    return <DataTableCell {...props}>'is a child'</DataTableCell>;
  }
  <DataTableCell {...props}>{children ? `$${children}` : ''}</DataTableCell>;
};

CustomPriceCell.displayName = DataTableCell.displayName;

/**
 * Custom Balance cell to display formatted date
 */
const CustomBalanceCell = ({ children, ...props }) => {
  const { item } = props;

  if (item.isChildren) {
    return (
      <DataTableCell
        title={`${getNumberWithCommas(getNumbersWithDecimalPlaces(children?.native, 5))} ${
          item?.token_lists?.name
        }`}
        {...props}
      >
        {item?.token_lists?.name}{' '}
        {getNumberWithCommas(getNumbersWithDecimalPlaces(children?.native, 3))} ({'$'}
        {getNumberWithCommas(getNumbersWithDecimalPlaces(item.balance.native * item.balance.price))}
        )
        {/* <span className="slds-col_padded">
          <img
            width="23"
            style={{ borderRadius: '50%' }}
            src={item?.token_lists?.logo_url}
            alt={`hedge-fund-maneger-${item?.token_lists?.name}`}
          />
        </span> */}
      </DataTableCell>
    );
  }

  const { portfolio_item_list } = item;
  const balanceArrayList = portfolio_item_list.map((protocol_assets) => {
    return protocol_assets.stats.net_usd_value;
    // Object.keys(protocol_assets.stats).map(
    //   (address_key) => protocol_assets.asset_dict[address_key]
    // );
  });
  const balanceArray = [].concat(...balanceArrayList);
  return (
    <DataTableCell {...props}>
      {'$' +
        getNumberWithCommas(
          getNumbersWithDecimalPlaces(
            balanceArray.reduce((partialSum, tobeAdded) => partialSum + tobeAdded, 0)
          )
        )}
    </DataTableCell>
  );
};

CustomBalanceCell.displayName = DataTableCell.displayName;

const TokensCell = ({ children, ...props }) => {
  const { item } = props;

  if (item.isChildren) {
    return (
      <DataTableCell title={item?.token_lists?.name}>
        <span style={{ paddingLeft: '0.2rem' }}>
          <img style={{ borderRadius: '50%' }} width="25" src={item.token_lists.logo_url} alt="" />
          <span className="slds-m-left_x-small">{item?.token_lists?.name}</span>
        </span>
      </DataTableCell>
    );
  }

  const { portfolio_item_list } = item;
  const arrayOfPortfolio_item_list = portfolio_item_list.map((p_item, index) => {
    return {
      name: p_item.name,
      supply_token_list: p_item.detail.supply_token_list,
    };
  });
  const logoImages = {};
  arrayOfPortfolio_item_list.forEach((items) => {
    items.supply_token_list.forEach((supply_token_list_item) => {
      if (supply_token_list_item.logo_url)
        logoImages[supply_token_list_item.symbol] = supply_token_list_item.logo_url;
    });
  });

  return (
    <td>
      {Object.keys(logoImages)
        .slice(0, 5)
        .map((key) => (
          <span key={key} style={{ paddingLeft: '0.2rem' }}>
            <img style={{ borderRadius: '50%' }} width="25" src={logoImages[key]} alt="" />
          </span>
        ))}
    </td>
  );
};
TokensCell.displayName = DataTableCell.displayName;

const RewardCell = ({ children, ...props }) => {
  const { item } = props;

  if (item.isChildren) {
    return <DataTableCell {...props}>{children}</DataTableCell>;
  }

  const { portfolio_item_list } = item;
  const arrayOfReward_token_list = portfolio_item_list.map((p_item, index) => {
    return {
      name: p_item.name,
      reward_token_list: p_item?.detail?.reward_token_list,
    };
  });
  const filteredRewardTokenLists = arrayOfReward_token_list.map((reward_token_lists) => {
    return reward_token_lists.reward_token_list.filter((rtl) => parseInt(rtl.amount) > 0);
  });

  let totalRewards = [];
  filteredRewardTokenLists.forEach((lists) => {
    return lists.forEach((reward_token_list) =>
      totalRewards.push(reward_token_list.amount * reward_token_list.price)
    );
  });
  return (
    <DataTableCell>
      $
      {getNumberWithCommas(
        getNumbersWithDecimalPlaces(
          totalRewards.reduce((partialSum, tobeAdded) => partialSum + tobeAdded, 0),
          0
        )
      )}
    </DataTableCell>
  );
};
RewardCell.displayName = DataTableCell.displayName;

const CustomDataTableChainCell = ({ children, ...props }) => {
  const { item } = props;
  return (
    <DataTableCell title={children} {...props}>
      <span className="slds-m-right_xx-small">
        {item.chain_logo && <img src={item.chain_logo} height="20" width="20" alt=""></img>}
      </span>
      {children}
    </DataTableCell>
  );
};

CustomDataTableChainCell.displayName = DataTableCell.displayName;

const LastTransactionDateData = ({ children, ...props }) => {
  const { item } = props;

  if (item.isChildren) {
    return (
      <DataTableCell title={children} {...props}>
        {moment.unix(Math.ceil(Number(children))).format('MM/D/yyyy HH:mm:ss')}
      </DataTableCell>
    );
  }

  return (
    <DataTableCell title={children} {...props}>
      {moment
        .unix(Math.ceil(Number(item.portfolio_item_list[0]?.update_at)))
        .format('MM/D/yyyy HH:mm:ss')}
    </DataTableCell>
  );
};
LastTransactionDateData.displayName = DataTableCell.displayName;

const WalletsData = ({ children, ...props }) => {
  const { item } = props;

  if (item.isChildren) {
    return <DataTableCell {...props}>{children}</DataTableCell>;
  }

  return <td></td>;
  // const { portfolio_item_list } = item;
  // let protocolWallets = portfolio_item_list
  //   .map((p_item) => p_item.name)
  //   .filter((value, index, self) => self.indexOf(value) === index);
  // return (
  //   <DataTableCell title={children} {...props}>
  //     {protocolWallets.join(', ')}
  //   </DataTableCell>
  // );
};

WalletsData.displayName = DataTableCell.displayName;

const Protocols = ({ account }) => {
  const dispatch = useDispatch();
  const { protocols, loading, pagination } = useSelector(protocolsSelector);
  const [sortColumn, setSortColumn] = useState('opportunityName');
  const [sortColumnDirection, setSortColumnDirection] = useState({
    opportunityName: 'asc',
  });
  const [items, setItems] = useState([]);
  const [childItems, setChildItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(undefined);
  const [paginated, setPaginated] = useState(0);

  const handleExpand = (event, row) => {
    //cloning the items to prevent mutation
    //...
    const _items = JSON.parse(JSON.stringify(items));

    /**
     * Setting the portfolio details for each wallet in the same index
     * as the wallet in the items array.
     */
    let childRows = JSON.parse(JSON.stringify(childItems));
    if (expandedRows?.id === row.id) {
      /**
       * If any one row is currently enabled
       * And same row is clicked, remove its child items
       */

      // Finding the index of the clicked row in the items array
      //...
      let index = _items.map((item) => item.id).indexOf(row.id);

      // Removing the child items from index + 1 to the number of children appended
      //...
      _items.splice(index + 1, childRows.length);

      _items[index].classNameRow = 'expandables';

      // Setting new Element in the items state
      //...
      setItems(_items);

      // Since it was the same element clicked, current expanded row is undefined
      setExpandedRows(undefined);
    } else {
      /**
       *
       * If the clicked row is entirely new
       * Check if there was existing expanded row
       *
       * Find the index of the current clicked row in the items array
       * Append the children row next to its index to make it appear like child in table
       *
       */

      if (expandedRows?.id) {
        /**
         * If there is expanded element in the table,
         * Find the index of the expanded row
         */
        let previousIndex = _items.map((item) => item.id).indexOf(expandedRows.id);

        /**
         * Remove the child element which was appended just below the parent index
         */
        _items.splice(previousIndex + 1, childRows.length);
      }

      childRows = [];

      let index = _items.map((item) => item.id).indexOf(row.id);
      const mainChildRowData = _items[index]?.portfolio_item_list;
      let childItemsArray = mainChildRowData.map((item) => {
        return item?.detail?.supply_token_list.map((supplyToken, index) => {
          return {
            id: _items[index]._id + '-' + supplyToken?.id + '-' + supplyToken?.symbol,
            name: supplyToken?.optimized_symbol || '---',
            isChildren: true,
            balance: {
              native: supplyToken?.amount,
              price: supplyToken?.price,
            },
            token_lists: {
              name: supplyToken?.optimized_symbol,
              logo_url: supplyToken?.logo_url,
            },
            rewards: item?.detail?.reward_token_list[index]
              ? `${item?.detail?.reward_token_list[index].symbol} ${getNumberWithCommas(
                  getNumberWithCommas(
                    getNumbersWithDecimalPlaces(item?.detail?.reward_token_list[index].amount, 3)
                  )
                )} ( $ ${getNumberWithCommas(
                  getNumbersWithDecimalPlaces(
                    item?.detail?.reward_token_list[index].amount *
                      item?.detail?.reward_token_list[index].price
                  )
                )})`
              : '0',
            last_transaction_date: supplyToken?.time_at,
            wallets: item?.name,
          };
        });
      });

      childRows = childRows.concat(...childItemsArray);
      childRows = childRows.filter((childItem) => childItem.balance.native > 0);

      let newItems = [
        ..._items.slice(0, index + 1),
        ...childRows.map((c, index) => {
          c.classNameRow = index % 2 === 0 ? `expanded-children even` : `expanded-children odd`;
          return c;
        }),
        ..._items.slice(index + 1),
      ];
      newItems[index].classNameRow = 'expanded';
      setExpandedRows({ id: row.id });
      setChildItems(childRows);
      setItems(newItems);
    }
  };

  useEffect(() => {
    if (account) {
      dispatch(getProtocolsOfAccount({ account: account }));
    }
  }, [account, dispatch]);

  useEffect(() => {
    if (protocols.length > 0) setItems(protocols);
  }, [protocols]);

  const handleSort = (sortColumn, ...rest) => {
    const sortProperty = sortColumn.property;

    const { sortDirection } = sortColumn;

    setSortColumn(sortProperty);
    setSortColumnDirection({
      [sortProperty]: sortDirection,
    });

    // sorting will actually be handled from api
    // so dispatch sort action in useEffect
    // dispatch(sortAccounte({sortDirection, sortProperty}))
    // and the update the state with sorted accounts lists this will be done by above useEffect
  };


  const handleLoadMore = () => {
    if (!loading && !isPageLoading) {
      setIsPageLoading(true);
      setTimeout(() => {
        if (pagination.pages <= 1 || paginated >= pagination.pages) {
          setHasMore(false);
          setIsPageLoading(false);
          return;
        }

        if (pagination.currentPage >= pagination.pages) {
          setHasMore(false);
          setIsPageLoading(false);
          return;
        }
        let url = new URLSearchParams(pagination.nextPageUrl);
        setPaginated(paginated + 1);
        dispatch(
          getProtocolsOfAccount({
            account: account,
            limit: url.get('limit'),
            offset: url.get('offset'),
          })
        );
        setIsPageLoading(false);
      }, 1000);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '1rem', marginTop: '10px' }}>
        <div style={{ width: '65%', height: '100vh' }}>
          <DataTable
            assistiveText={{
              actionsHeader: 'actions',
              columnSort: 'sort this column',
              columnSortedAscending: 'asc',
              columnSortedDescending: 'desc',
              selectAllRows: 'Select all rows',
              selectRow: 'Select this row',
            }}
            fixedHeader
            fixedLayout
            items={items}
            id="Protocols-Table"
            onSort={handleSort}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            noRowHover
          >
            <DataTableColumn
              isSorted={sortColumn === 'name'}
              label="Walet"
              primaryColumn
              property="name"
              sortable
              sortDirection={sortColumnDirection.name}
            >
              <ProtocolCell className="test--class--for" handleExpand={handleExpand} />
            </DataTableColumn>

            <DataTableColumn sortable isDefaultSortDescending label="Balance" property="balance">
              <CustomBalanceCell />
            </DataTableColumn>

            <DataTableColumn
              sortable
              isDefaultSortDescending
              label="Token Balance"
              property="token_balance"
            >
              <TokensCell />
            </DataTableColumn>
            <DataTableColumn
              sortable
              isDefaultSortDescending
              label="Price 24 hour change"
              property="price"
            >
              <RewardCell />
            </DataTableColumn>

            <DataTableColumn
              sortable
              isDefaultSortDescending
              label="Balance 24 hour change"
              property="balance_24"
            >
              <LastTransactionDateData />
            </DataTableColumn>
          </DataTable>
        </div>
        <div style={{ width: '35%' }}>
          <div style={{ flexBasis: '50%', margin: '0px' }} className="home_card_two">
            <div className="" style={{ marginBottom: '1rem' }}>
              <span className="heading_text">Protocols Split</span>
              <span
                style={{
                  float: 'right',
                  color: '#0070D2',
                  fontWeight: 400,
                  fontSize: 13,
                  border: '1px solid blue',
                  padding: '7px 12px',
                  borderRadius: '5px',
                }}
              >
                Pie Chart &nbsp;
                <img src={LightningIcon} alt="lightningIcon" />
              </span>
            </div>
            <div style={{ margin: '0px 0px 20px 0px' }}>
              <BarChartComponent />
            </div>
            <div className="view-assets" style={{ color: '#006DCC' }}>
              View All Assets
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Protocols;
